import { useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback } from "react";
import type { GroupBase, SelectInstance } from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import makeSubtitleOptionComponent from "components/Shared/Select/makeSubtitleOptionComponent";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import useSelectStyles from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { useCurrentBrandId, useCurrentUserData } from "utils/currentUserHooks";
import { assertPresence } from "utils/assertions";
import { UserFilter } from "__generated__/gql/graphql";
import useLatestRef from "utils/useLatestRef";
import { useOptionalCombineFilters } from "utils/useCombineFilters";

const optionFragment = gql(/* GraphQL */ `
  fragment UserSelectOption on User {
    id
    fullName
    email
  }
`);

const valueQuery = gql(/* GraphQL */ `
  query UserSelectValue($ids: [UUID!]!) {
    usersList(filter: { id: { in: $ids } }) {
      ...UserSelectOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query UserSelectOptions($filter: UserFilter!, $limit: Int = 5) {
    users(filter: $filter, orderBy: LAST_NAME_ASC, first: $limit) {
      totalCount
      nodes {
        ...UserSelectOption
      }
    }
  }
`);

type Option = DocumentType<typeof optionFragment>;

const UserOption = makeSubtitleOptionComponent<Option>({
  renderSubtitle: ({ option }) => option.email,
});

function noOptionsMessage({ inputValue }: { inputValue: string }) {
  if (inputValue?.length > 1)
    return (
      <>
        No users found containing: <strong>{inputValue}</strong>
      </>
    );
  return "Enter at least two characters to search…";
}

type Props<IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    filter?: UserFilter;
    isAdmin?: boolean;
  };

function UserSelectInner<IsMulti extends boolean = false>(
  {
    isAdmin,
    isMulti,
    onChange,
    value: idValue,
    filter: filterProp,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();
  const client = useApolloClient();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.usersList),
    isMulti,
    labelKey: "fullName",
    onChange,
    query: valueQuery,
    value: idValue,
  });

  const latestFilterPropRef = useLatestRef(filterProp);

  const asyncFetchProps = useAsyncFetchOptions({
    fetchOptions: useCallback(
      (inputValue) => {
        const baseFilter: UserFilter = {
          or: [
            {
              fullName: { includesInsensitive: inputValue },
            },
            {
              email: { includesInsensitive: inputValue },
            },
          ],
        };

        const filter: UserFilter = latestFilterPropRef.current
          ? { and: [baseFilter, latestFilterPropRef.current] }
          : baseFilter;

        const context = (() => {
          if (isAdmin) return ADMIN_CONTEXT;
          assertPresence(brandId);
          return getActiveBrandIdContext(brandId);
        })();

        return client
          .query({
            query,
            variables: { filter },
            context,
          })
          .then(({ data }) => {
            assertPresence(data.users, "Could not get users from response.");
            const { nodes, totalCount } = data.users;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [client, isAdmin, brandId, latestFilterPropRef],
    ),
  });

  return (
    <AsyncSelect<Option, IsMulti>
      {...{
        ...styleProps,
        ...idValueProps,
        ...asyncFetchProps,
        ref,
        isMulti,
        noOptionsMessage,
      }}
      placeholder={`Select user${isMulti ? "s" : ""}…`}
      {...rest}
      components={{
        ...styleProps.components,
        ...asyncFetchProps.components,
        Option: UserOption,
        ...rest.components,
      }}
    />
  );
}

const UserSelect = forwardRef(UserSelectInner);
export default UserSelect;

export function getBrandOrAccessAllBrandsUserFilter(brandId: UUID): UserFilter {
  return {
    or: [
      { brandUsers: { some: { brandId: { equalTo: brandId } } } },
      { isStaff: { equalTo: true }, canAccessAllBrands: { equalTo: true } },
    ],
  };
}

type UserForBrandSelectProps<IsMulti extends boolean = false> = Omit<
  Props<IsMulti>,
  "isAdmin"
>;

function UserForBrandSelectInner<IsMulti extends boolean = false>(
  { filter: filterProp, ...rest }: UserForBrandSelectProps<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const user = useCurrentUserData();
  const brandId = useCurrentBrandId();

  const isAdmin = !!user?.isStaff && !!user?.canAccessAllBrands;

  const filter = useOptionalCombineFilters(
    filterProp,
    brandId ? getBrandOrAccessAllBrandsUserFilter(brandId) : undefined,
  );

  return <UserSelect ref={ref} {...rest} isAdmin={isAdmin} filter={filter} />;
}

export const UserForBrandSelect = forwardRef(UserForBrandSelectInner);
